import { Time } from '@internationalized/date';
import {
	Button,
	Picker,
	PickerItem,
	Radio,
	RadioGroup,
	Tab,
	TabList,
	TabPanel,
	Tabs,
	Text,
	TimeField,
} from '@react-spectrum/s2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import {
	CADENCE_MIN_NOTCH,
	CADENCE_NOTCHES,
	WEEKDAYS,
	cadenceEquals,
	cadenceToNotch,
	describeCadence,
	formatCadence,
	formatNextSend,
	formatWeekday,
	isValidSendTime,
	notchToCadence,
	resolveWeekday,
	summarizeVolume,
	toTimingEntry,
	weekdayOf,
	type Cadence,
	type Weekday,
} from '@/spaces/chat/panels/cadenceSchedule';
import { SeededJobPanel } from '@/spaces/chat/panels/SeededJobPanel';
import {
	type DailySmsHistoryItem,
	describeDelivery,
	truncateDeliveryDetail,
} from '@/spaces/chat/panels/smsDelivery';

import type { PanelProcess } from '../../../../../bridge/types/state.ts';

interface DailySmsSchedule {
	every: number;
	unit: string;
	at: string | null;
	timezone: string | null;
	day_of_week?: string[] | null;
	next_fire_at?: string | null;
}

interface DailySmsJobPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

const TABS = ['Settings', 'Recent Texts'] as const;

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<h3
		className="mb-3 text-[11px] font-semibold tracking-[0.12em]
			text-(--text-muted) uppercase"
	>
		{children}
	</h3>
);

const maskPhoneNumber = (value?: string): string | null => {
	if (!value) return null;
	const digits = value.replace(/\D/g, '');
	if (digits.length < 4) return null;
	return `*** ${digits.slice(-4)}`;
};

const parseSendTime = (at: string | null | undefined): Time => {
	if (at && isValidSendTime(at)) {
		const [hour, minute] = at.split(':').map(Number);
		return new Time(hour, minute);
	}
	return new Time(9, 0);
};

const toSendTime = (value: Time): string =>
	`${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`;

// The zone is auto-captured from the browser, so it reads like a place name
// ("America/Los_Angeles"). Show the city, not the database path.
const humanizeZone = (zone: string | null): string | null =>
	zone ? zone.split('/').pop()?.replace(/_/g, ' ') || zone : null;

export const DailySmsJobPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
}: DailySmsJobPanelProps) => {
	const userId = useUserId();
	const api = useBridgewayApi();
	const { settings } = useUserSettings();
	const [history, setHistory] = useState<DailySmsHistoryItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<Key>('Settings');

	// `schedule` is server truth, re-read after every write. The draft state is
	// what the controls edit; dirtiness is the difference between the two.
	const [schedule, setSchedule] = useState<DailySmsSchedule | null>(null);
	const [draftNotch, setDraftNotch] = useState<number | null>(null);
	const [draftTime, setDraftTime] = useState<Time | null>(null);
	const [draftWeekday, setDraftWeekday] = useState<Weekday | null>(null);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const isActive = panelProcess.status === 'active';
	const isOptedOut = Boolean(settings?.sms_opted_out_at);
	// The settings route always answers with a document, so an absent `settings`
	// never means "this account has none". It means the request has not answered
	// yet, or it failed and nothing retries it. Until it answers, "not opted out"
	// is a guess, and a guess must not become a promise.
	//
	// Tested for presence, not against `null`: the provider starts at `null`, but
	// `RestApi` resolves `undefined` for a 204 or an empty body, and the declared
	// type hides that.
	const isOptOutKnown = Boolean(settings);

	useEffect(() => {
		let isCurrent = true;
		if (!userId) {
			setHistory((current) => (current.length > 0 ? [] : current));
			setLoading(false);
			return () => {
				isCurrent = false;
			};
		}

		setLoading(true);
		api
			.get<DailySmsHistoryItem[]>(
				`/api/user/${userId}/daily_sms/history?limit=3`,
			)
			.then((rows) => {
				if (!isCurrent) return;
				setHistory(Array.isArray(rows) ? rows : []);
			})
			.catch((err) => {
				if (!isCurrent) return;
				logger.error('DailySmsJobPanel history fetch failed', err);
				setHistory([]);
			})
			.finally(() => {
				if (isCurrent) setLoading(false);
			});

		return () => {
			isCurrent = false;
		};
	}, [api, userId]);

	// Kept out of `loading` on purpose: ChatPanelLayout nulls the footer action
	// slot whenever isLoading is set, so folding this fetch into it would flash
	// the Save button out on every refresh.
	//
	// Re-runs when the job is switched on or off, because pausing and resuming
	// makes the server recompute next_fire_at. `isActive` only changes once the
	// pause/resume call has come back, so the refetch reads the new value rather
	// than racing it.
	useEffect(() => {
		let isCurrent = true;
		if (!userId) return;
		api
			.get<DailySmsSchedule>(`/api/user/${userId}/daily_sms/schedule`)
			.then((row) => {
				if (!isCurrent || !row) return;
				setSchedule(row);
			})
			.catch((err) => {
				if (!isCurrent) return;
				logger.error('DailySmsJobPanel schedule fetch failed', err);
			});
		return () => {
			isCurrent = false;
		};
	}, [api, userId, isActive]);

	// Read once per mount so the default weekday cannot shift under the user
	// mid-session.
	const today = useMemo(() => new Date(), []);

	const savedCadence = useMemo<Cadence>(
		() =>
			schedule
				? notchToCadence(
						cadenceToNotch({
							every: schedule.every,
							unit: schedule.unit === 'week' ? 'week' : 'day',
						}),
					)
				: notchToCadence(CADENCE_MIN_NOTCH),
		[schedule],
	);
	const savedNotch = cadenceToNotch(savedCadence);
	const savedTime = parseSendTime(schedule?.at);
	// A schedule saved before this control existed has no weekday of its own, so
	// today stands in until the user picks one.
	const savedWeekday = resolveWeekday(schedule?.day_of_week, weekdayOf(today));

	const notch = draftNotch ?? savedNotch;
	const cadence = notchToCadence(notch);
	const sendTime = draftTime ?? savedTime;
	const weekday = draftWeekday ?? savedWeekday;
	const isWeekly = cadence.unit === 'week';

	const isDirty =
		schedule != null &&
		(!cadenceEquals(cadence, savedCadence) ||
			toSendTime(sendTime) !== toSendTime(savedTime) ||
			// A weekday edit only counts while a weekly cadence is selected; it is
			// not part of what a daily schedule stores.
			(isWeekly && weekday !== savedWeekday));

	const zone = schedule?.timezone ?? null;
	const nextSend = formatNextSend(schedule?.next_fire_at, zone ?? 'UTC');

	const handleSave = useCallback(() => {
		if (!userId || !schedule || !isDirty) return;
		setSaving(true);
		setSaveError(null);
		const body = toTimingEntry(
			cadence,
			toSendTime(sendTime),
			zone ?? 'America/Los_Angeles',
			weekday,
		);
		api
			.patch<DailySmsSchedule>(`/api/user/${userId}/daily_sms/schedule`, body)
			.then((row) => {
				// Re-seed from what the server actually stored, not from the draft:
				// the scheduler owns the phase, so only it knows the next send.
				if (row) setSchedule(row);
				setDraftNotch(null);
				setDraftTime(null);
				setDraftWeekday(null);
			})
			.catch((err) => {
				logger.error('DailySmsJobPanel cadence save failed', err);
				setSaveError("That didn't save. Try again in a moment.");
			})
			.finally(() => setSaving(false));
	}, [api, cadence, isDirty, schedule, sendTime, userId, weekday, zone]);

	const actionRenderer = useCallback(
		() => (
			<Button
				variant="accent"
				isDisabled={!isDirty || saving}
				onPress={handleSave}
			>
				<Text>{saving ? 'Saving…' : 'Save schedule'}</Text>
			</Button>
		),
		[handleSave, isDirty, saving],
	);

	return (
		<SeededJobPanel
			panelProcess={panelProcess}
			fallbackTitle="Daily Updates"
			isLoading={loading}
			onBack={onBack}
			onClose={onClose}
			style={style}
			showDescription={false}
			actionRenderer={schedule ? actionRenderer : undefined}
		>
			<Tabs
				aria-label="Daily Updates"
				selectedKey={activeTab}
				onSelectionChange={setActiveTab}
				UNSAFE_className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<div className="flex-shrink-0">
					<TabList>
						{TABS.map((tab) => (
							<Tab key={tab} id={tab}>
								{tab}
							</Tab>
						))}
					</TabList>
				</div>
				<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto">
					<TabPanel id="Settings">
						<div className="mt-5 flex flex-col gap-6">
							<section>
								<SectionHeading>How It Works</SectionHeading>
								<div className="text-sm text-(--text-primary)">
									Bridgeway checks for pending review items and new insights on
									the schedule you set. If there is something useful to review,
									it sends one concise text update — and stays quiet when there
									is nothing worth saying. Use the status switch to turn the
									texts off entirely.
								</div>
							</section>

							{schedule && (
								<section data-testid="daily-sms-schedule">
									<SectionHeading>Sending Schedule</SectionHeading>
									{/* Every choice is listed, so the reader can compare all five
									    before touching anything — a slider showed only the one
									    already selected and named the rest solely while dragging. */}
									<RadioGroup
										aria-label="How often the daily text arrives"
										value={String(notch)}
										isDisabled={!isActive || saving}
										isEmphasized
										size="M"
										onChange={(next) => setDraftNotch(Number(next))}
									>
										{CADENCE_NOTCHES.map((option, index) => (
											<Radio key={index} value={String(index)}>
												<span className="flex flex-col">
													<span className="font-semibold">
														{formatCadence(option)}
													</span>
													<span className="text-xs text-(--text-muted)">
														{summarizeVolume(option)}
													</span>
												</span>
											</Radio>
										))}
									</RadioGroup>

									<div className="mt-5 flex flex-wrap items-end gap-3">
										{/* Only a weekly cadence has a weekday to choose. Shown
										    because the day is otherwise decided by whenever Save
										    happened, which the reader can neither see nor change. */}
										{isWeekly && (
											<Picker
												label="Day of week"
												size="M"
												data-testid="cadence-weekday"
												selectedKey={weekday}
												isDisabled={!isActive || saving}
												onSelectionChange={(key) =>
													setDraftWeekday(key as Weekday)
												}
											>
												{WEEKDAYS.map((day) => (
													<PickerItem key={day} id={day}>
														{formatWeekday(day)}
													</PickerItem>
												))}
											</Picker>
										)}
										<TimeField
											label="Time of day"
											hourCycle={12}
											size="M"
											value={sendTime}
											isDisabled={!isActive || saving}
											onChange={(value) => value && setDraftTime(value)}
										/>
										{zone && (
											<span
												data-testid="cadence-zone"
												className="pb-2 text-xs text-(--text-muted)"
											>
												{humanizeZone(zone)} time
											</span>
										)}
									</div>

									<div className="mt-4 border-t border-black/[0.06] pt-3">
										<p className="text-sm text-(--text-primary)">
											{describeCadence(cadence, isWeekly ? weekday : undefined)}
										</p>
										{!isActive ? (
											<p
												data-testid="cadence-next-send"
												className="mt-1 text-sm text-(--text-muted)"
											>
												Paused — no texts will be sent.
											</p>
										) : isOptedOut ? (
											// The schedule can be switched on and still reach nobody:
											// the customer texted STOP, so promising a next text here
											// would be a promise we cannot keep.
											<p
												data-testid="cadence-next-send"
												className="mt-1 text-sm text-(--text-muted)"
											>
												Texts are stopped for this number. Text START to resume.
											</p>
										) : (
											isOptOutKnown &&
											nextSend && (
												<p
													data-testid="cadence-next-send"
													className="mt-1 text-sm text-(--text-muted)"
												>
													Next text {nextSend}
												</p>
											)
										)}
										{saveError && (
											<p
												data-testid="cadence-save-error"
												className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm
													text-red-600"
											>
												{saveError}
											</p>
										)}
									</div>
								</section>
							)}
						</div>
					</TabPanel>

					<TabPanel id="Recent Texts">
						<div className="mt-5">
							{history.length === 0 ? (
								<p className="text-sm text-(--text-muted)">
									No daily update texts have been sent yet.
								</p>
							) : (
								<div className="flex flex-col gap-2">
									{history.map((row) => {
										const maskedTo = maskPhoneNumber(row.to);
										const deliveryDetail =
											row.status === 'undelivered'
												? truncateDeliveryDetail(row.delivery_detail)
												: null;
										return (
											<div
												key={row.id}
												data-testid="daily-sms-history-row"
												data-row-id={row.id}
												className="flex flex-col gap-1 rounded-lg border
													border-(--glass-border) px-3 py-2"
											>
												<div
													className="flex items-center gap-2 text-xs
														text-(--text-muted)"
												>
													<span>{describeDelivery(row)}</span>
													{maskedTo && <span>to {maskedTo}</span>}
													{deliveryDetail && <span>{deliveryDetail}</span>}
												</div>
												{row.message && (
													<p
														className="line-clamp-2 text-sm
															text-(--text-primary)"
													>
														{row.message}
													</p>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>
					</TabPanel>
				</div>
			</Tabs>
		</SeededJobPanel>
	);
};
